import { NestedFilterInput, NestedFilterItem } from '@apeiron/common'
import {
  ConstellationFilterInput,
  ConstellationInFilter,
  StarFilterInput
} from '@apeiron/star'
import { styled } from '@mui/material'
import NestedFilter from '@src/components/share/filter/nestedFilter/NestedFilter'
import NestedFilterWrapper from '@src/components/share/filter/nestedFilter/NestedFilterWrapper'
import ConstellationSizeFilter from '@src/components/stars/list/filter/constellationFilter/ConstellationSizeFilter'
import ConstellationStarCountFilter from '@src/components/stars/list/filter/constellationFilter/ConstellationStarCountFilter'
import StarConstellationItem from '@src/components/stars/list/filter/constellationFilter/StarConstellationItem'
import { useConstellationsContext } from '@src/contexts/star/ConstellationsContext'
import * as R from 'ramda'
import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DEFAULT_FILTER_INPUT = {
  types: [],
  minStarCount: 0,
  maxStarCount: 20
}

const StyledStarConstellationItem = styled(StarConstellationItem)`
  margin: 0px 5px;
`

const StarConstellationFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterInputChange } = props

  const { t } = useTranslation()

  const field = 'constellationComposes'

  const [selectedItems, setSelectedItems] = useState<NestedFilterItem[]>([])

  const { allConstellations, searchConstellations, sortConstellations } =
    useConstellationsContext()

  const handleOnFilterInputChange = useCallback(
    (input: NestedFilterInput) => {
      const typedInput = input as StarFilterInput

      onFilterInputChange(typedInput)
    },
    [onFilterInputChange]
  )

  const handleOnRenderItem = useCallback((item: NestedFilterItem) => {
    const constellation = item as ConstellationInFilter

    return <StyledStarConstellationItem constellation={constellation} />
  }, [])

  const handleOnRenderPopupFilter = useCallback(
    (
      input: NestedFilterInput,
      onFilterItem: (filterInput: NestedFilterInput) => void
    ) => {
      const typedInput = input as ConstellationFilterInput

      return (
        <>
          <ConstellationStarCountFilter
            filterInput={typedInput}
            onFilterChange={onFilterItem}
          />
          <ConstellationSizeFilter
            filterInput={typedInput}
            onFilterChange={onFilterItem}
          />
        </>
      )
    },
    []
  )

  const handleOnSearchByText = useCallback((item: NestedFilterItem) => {
    const typedItem = item as ConstellationInFilter

    return `${typedItem.name} ${typedItem.type.name} ${typedItem.galaxy.name}`
  }, [])

  const handleOnSearchItems = useCallback(
    (input: NestedFilterInput) => {
      const typedInput = input as StarFilterInput

      return searchConstellations({
        uniqueIds: R.propOr([], field, typedInput)
      })
    },
    [searchConstellations]
  )

  const handleOnSearchInnerItems = useCallback(
    (input: NestedFilterInput) => {
      const typedInput = input as ConstellationFilterInput

      return searchConstellations(typedInput)
    },
    [searchConstellations]
  )

  const handleOnSortConstellations = useCallback(
    (items: NestedFilterItem[]) => {
      return sortConstellations(items as ConstellationInFilter[])
    },
    [sortConstellations]
  )

  const handleOnReset = useCallback(() => {
    const newFilter = {
      ...filterInput,
      [field]: undefined
    }

    setSelectedItems([])
    onFilterInputChange(newFilter)
  }, [field, filterInput, onFilterInputChange])

  return (
    <NestedFilterWrapper
      className={className}
      title={t('star.list.filter.constellation.title')}
      fields={[field]}
      filterInput={filterInput}
      handleOnReset={handleOnReset}
    >
      <NestedFilter
        allItems={allConstellations}
        className={className}
        defaultSearchParam={DEFAULT_FILTER_INPUT}
        field={field}
        filterInput={filterInput}
        searchPlaceholder={t(
          'star.list.filter.constellation.search.placeholder'
        )}
        onFilterInputChange={handleOnFilterInputChange}
        renderItem={handleOnRenderItem}
        renderPopupFilter={handleOnRenderPopupFilter}
        searchByText={handleOnSearchByText}
        searchItems={handleOnSearchItems}
        searchInnerItems={handleOnSearchInnerItems}
        sortItems={handleOnSortConstellations}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    </NestedFilterWrapper>
  )
}

type Props = {
  className?: string
  filterInput: StarFilterInput
  onFilterInputChange: (input: StarFilterInput) => void
}

export default StarConstellationFilter
